import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';
import fetchCrmFailover from '@swf/core/utils/failovers';

export interface CrmZoneData {
  contents?: string[];
}

interface CrmZone {
  [zoneId: string]: CrmZoneData;
}

interface TrackCode {
  trackCodeAssigned: boolean;
}

export type CrmState = CrmZone | TrackCode;

interface CRMModel extends ModelConfig<CrmState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const reducers = {
  addZone: (state: CrmState, payload: CrmZone): CrmState => ({ ...state, ...payload }),
  setTrackCode: (state: CrmState): CrmState => ({ ...state, trackCodeAssigned: true })
};

const effects = {
  async fetchZone({ zoneId }: { zoneId: string }, { dispatch, request, rootState }: EffectParams) {
    const brand = select.site.getBrand(rootState);
    let contents;
    try {
      const { body } = await request({
        service: 'crm.zone',
        body: { zoneId },
        headers: { brand: brand.toString() }
      });
      ({ contents } = body);
    } catch (error) {
      if (canUseDOM) {
        const failoverContent = await fetchCrmFailover(zoneId);
        contents = failoverContent ? [failoverContent] : [];
      }
    } finally {
      dispatch.crm.addZone({
        [zoneId]: {
          contents: contents || []
        }
      });
    }
  },
  async assignTrackCode(payload: void, { dispatch, request }: EffectParams) {
    try {
      await request({ service: 'crm.assigntrackcode', body: payload });
      dispatch.crm.setTrackCode();
    } catch (error) {
      // do nothing
    }
  }
};

const emptyZone = Object.create(null);

const selectors = (slice: Slicer<CrmState>, createSelector: SelectorCreator) => ({
  getZone() {
    return createSelector(
      slice,
      (rootState: RootState, zoneId: string) => zoneId,
      (state, zoneId): CrmZoneData => state[zoneId as keyof CrmState] || emptyZone
    );
  },
  isTrackCodeAssigned() {
    return slice((state: CrmState) => !!state.trackCodeAssigned);
  }
});

const crm: CRMModel = {
  state: { trackCodeAssigned: false },
  reducers,
  effects,
  selectors
};

export default crm;
